// readings-archive.js
class ReadingsSlider extends HTMLElement {
  constructor() {
    super();

    this.slides = [];
    this.dots = [];
    this.currentIndex = 0;

    // drag state
    this.isDragging = false;
    this.isHorizontal = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.translateX = 0;
    this.dragThreshold = 10;

    this.autoplayInterval = null;

    // bind
    this.nextSlide = this.nextSlide.bind(this);
    this.prevSlide = this.prevSlide.bind(this);
    this.goToSlide = this.goToSlide.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragMove = this.handleDragMove.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.removeEventListeners();
  }

  init() {
    this.slides = Array.from(this.querySelectorAll('.readings-slide'));
    this.dots = Array.from(this.querySelectorAll('.readings-slider__dot'));
    this.container = this.querySelector('.readings-slider__container');
    this.prevBtn = this.querySelector('.readings-slider__nav--prev');
    this.nextBtn = this.querySelector('.readings-slider__nav--next');

    if (this.slides.length) {
      this.updateSlides();
      this.updateDots();
    }

    this.addEventListeners();

    if (this.dataset.autoplay === 'true') {
      this.startAutoplay();
    }
  }

  addEventListeners() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.prevSlide);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.nextSlide);

    this.dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.index);
        this.goToSlide(index);
      });
    });

    // mouse
    this.container.addEventListener('mousedown', this.handleDragStart);
    this.container.addEventListener('mousemove', this.handleDragMove);
    document.addEventListener('mouseup', this.handleDragEnd);

    // touch（关键：passive false）
    this.container.addEventListener('touchstart', this.handleDragStart, { passive: true });
    this.container.addEventListener('touchmove', this.handleDragMove, { passive: false });
    this.container.addEventListener('touchend', this.handleDragEnd);

    document.addEventListener('keydown', this.handleKeyDown);

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.dataset.autoplay === 'true') this.startAutoplay();
    });
  }

  removeEventListeners() {
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.prevSlide);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.nextSlide);

    this.container.removeEventListener('mousedown', this.handleDragStart);
    this.container.removeEventListener('mousemove', this.handleDragMove);
    document.removeEventListener('mouseup', this.handleDragEnd);

    this.container.removeEventListener('touchstart', this.handleDragStart);
    this.container.removeEventListener('touchmove', this.handleDragMove);
    this.container.removeEventListener('touchend', this.handleDragEnd);

    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleDragStart(e) {
    this.isDragging = true;
    this.isHorizontal = false;

    if (e.type === 'touchstart') {
      this.startX = e.touches[0].pageX;
      this.startY = e.touches[0].pageY;
    } else {
      this.startX = e.pageX;
    }

    this.currentX = this.startX;
    this.translateX = 0;

    this.container.classList.add('readings-slider__container--dragging');
    this.stopAutoplay();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    // touch：判断方向
    if (e.type === 'touchmove') {
      const touch = e.touches[0];
      const dx = touch.pageX - this.startX;
      const dy = touch.pageY - this.startY;

      // 还没判断方向
      if (!this.isHorizontal) {
        // 纵向滚动 → 立刻放行
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > this.dragThreshold) {
          this.isDragging = false;
          this.container.classList.remove('readings-slider__container--dragging');
          return;
        }

        // 横向拖拽
        if (Math.abs(dx) > this.dragThreshold) {
          this.isHorizontal = true;
        } else {
          return;
        }
      }

      // 横向时才阻止默认滚动
      e.preventDefault();
      this.currentX = touch.pageX;
    } else {
      // mouse
      this.currentX = e.pageX;
    }

    this.translateX = this.currentX - this.startX;
    this.updateSlidePosition();
  }

  handleDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.container.classList.remove('readings-slider__container--dragging');

    const slideWidth = this.container.offsetWidth;
    const threshold = slideWidth * 0.2;

    if (this.translateX > threshold) {
      this.prevSlide();
    } else if (this.translateX < -threshold) {
      this.nextSlide();
    }

    this.translateX = 0;
    this.updateSlidePosition();

    if (this.dataset.autoplay === 'true') {
      this.startAutoplay();
    }
  }

  updateSlidePosition() {
    const slideWidth = this.container.offsetWidth;
    const offset = (this.translateX / slideWidth) * 100;

    this.slides.forEach((slide, index) => {
      if (index !== this.currentIndex) {
        const distance = Math.abs(index - this.currentIndex);
        const blur = Math.max(8 - distance * 2, 0);
        const img = slide.querySelector('.readings-slide__image-wrapper');
        if (img) {
          img.style.filter = `blur(${blur}px)`;
          img.style.transform = `translateX(${offset}%)`;
        }
      }
    });
  }

  handleKeyDown(e) {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === 'ArrowLeft') this.prevSlide();
    if (e.key === 'ArrowRight') this.nextSlide();
    if (e.key === 'Home') this.goToSlide(0);
    if (e.key === 'End') this.goToSlide(this.slides.length - 1);
  }

  nextSlide() {
    this.goToSlide((this.currentIndex + 1) % this.slides.length);
  }

  prevSlide() {
    this.goToSlide((this.currentIndex - 1 + this.slides.length) % this.slides.length);
  }

  goToSlide(index) {
    if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;

    const oldIndex = this.currentIndex;
    this.currentIndex = index;

    this.updateSlides();
    this.updateDots();

    this.dispatchEvent(
      new CustomEvent('slideChange', {
        detail: { oldIndex, newIndex: index },
        bubbles: true
      })
    );
  }

  updateSlides() {
    this.slides.forEach((slide, index) => {
      const img = slide.querySelector('.readings-slide__image-wrapper');

      if (index === this.currentIndex) {
        slide.classList.add('readings-slide--active');
        slide.removeAttribute('aria-hidden');
        slide.setAttribute('aria-live', 'polite');
        if (img) {
          img.style.filter = 'blur(0)';
          img.style.transform = 'translateX(0)';
        }
      } else {
        slide.classList.remove('readings-slide--active');
        slide.setAttribute('aria-hidden', 'true');
        slide.removeAttribute('aria-live');
      }
    });
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('readings-slider__dot--active', index === this.currentIndex);
      if (index === this.currentIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  startAutoplay() {
    const speed = Number(this.dataset.autoplaySpeed) || 4000;
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => this.nextSlide(), speed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
}

if (!customElements.get('readings-slider')) {
  customElements.define('readings-slider', ReadingsSlider);
}
document.querySelectorAll('a[href^="#"]').forEach(link => {
  link.addEventListener('click', e => {
    const id = link.getAttribute('href');
    const target = document.querySelector(id);
    if (!target) return;

    e.preventDefault();
    const y = target.getBoundingClientRect().top + window.pageYOffset - 100;

    window.scrollTo({
      top: y,
      behavior: 'smooth'
    });
  });
});

